import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Alert, Button, Card, Col, Drawer, Row, Spin, Tag, Typography } from "antd";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HomeIcon from "@mui/icons-material/Home";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import styled from "styled-components";
import { buscarAnuncioPropriedeByCotaUsuario } from "../../services/buscarAnuncioPropriedeByCotaUsuarioService";
import { buscarCotaUsuarioByIdService } from "../../services/buscarCotaUsuarioByIdService";
import { buscarPeriodosByCotaUsuario } from "../../services/buscarPeriodosByCotaUsuarioService";
import { buscarVisualizacoesByAnuncioIdService } from "../../services/buscarVisualizacoesByAnuncioIdService";

const Page = styled.main`
  box-sizing: border-box;
  display: block;
  width: min(1180px, calc(100vw - 48px));
  max-width: 1180px;
  flex: 0 0 auto;
  align-self: center;
  margin: 0 auto;
  padding: 28px 24px 56px;
  color: #102b52;
  .ant-col { min-width: 0; }

  @media (min-width: 1200px) {
    width: min(1280px, calc(100vw - 80px));
    padding: 40px 32px 64px;
  }

  @media (max-width: 600px) { width: calc(100vw - 20px); padding: 20px 14px 40px; }
`;
// Usar div evita herdar a regra global do projeto para a tag HTML <header>.
const Header = styled.div`
  display:flex;gap:14px;align-items:flex-start;margin:14px 0 22px;
  @media (min-width: 1200px) { margin: 18px 0 30px; }
`;
const IconBox = styled.span`width:40px;height:40px;flex:0 0 40px;border-radius:50%;background:#eaf6fd;color:#0878ba;display:inline-flex;align-items:center;justify-content:center;`;
const Section = styled(Card)`
  width:100%;height:100%;border-radius:12px;
  .ant-card-head{min-height:64px}.ant-card-head-title{white-space:normal}.ant-card-body{padding:20px}
  @media (min-width: 1200px) { .ant-card-body{padding:24px} }
`;
const Label = styled(Typography.Text)`display:block;font-size:12px;color:#71809a;`;
const Value = styled(Typography.Text)`display:block;margin-top:4px;color:#102b52;font-size:16px;font-weight:600;`;
const MainGrid = styled(Row)`
  width: 100%;
  > .ant-col { display: flex; }

  @media (min-width: 1200px) {
    > .ant-col { min-height: 248px; }
  }
`;

const CardTitle = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}><IconBox>{icon}</IconBox><span>{children}</span></span>
);

// A página de informações úteis só possui conteúdo para estes empreendimentos hoje.
const INFO_EMPREENDIMENTOS = new Set([
  "Búzios Beach Resort", "Château Du Golden", "Dom Pedro Laguna", "Golden Gramado Resort Laghetto",
  "Hotel Nacional", "La Bas", "Ondas Praia Resort", "Praias do Lago Eco Resort", "Quinta Santa Barbara Eco Resort",
  "Salinas Exclusive Resort", "Salinas Park Resort", "Salinas Premium Resort", "Solar Pedra da Ilha",
]);

const statusLabel = (status?: string) => {
  if (status === "AUTENTICADA_NIVEL2") return "Autenticada nível 2";
  if (status === "AUTENTICADA_NIVEL1") return "Autenticada nível 1";
  return "Cadastro concluído";
};
const firstValue = (source: Record<string, any>, keys: string[]) => keys.map((key) => source[key]).find((value) => value !== undefined && value !== null && value !== "");
const formatDate = (value?: unknown) => {
  if (!value) return undefined;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC" }).format(date);
};
const formatCurrency = (value?: unknown) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(amount) : undefined;
};
const titularidadeLabel = (vinculos?: Array<{ tipoVinculo?: string; principal?: boolean | number }>) => {
  const vinculoPrincipal = vinculos?.find((vinculo) => vinculo.principal === true || vinculo.principal === 1);

  switch (vinculoPrincipal?.tipoVinculo) {
    case "TITULAR": return "Própria";
    case "CONJUGE": return "Cônjuge";
    case "FAMILIAR": return "Familiar";
    case "REVENDEDOR": return "Revenda";
    default: return "Não informada";
  }
};

export default function ViewDetalhesCota({ cotaUsuarioId }: { cotaUsuarioId?: number }) {
  const navigate = useNavigate();
  const [params] = useQueryParams({ cotaUsuarioId: NumberParam, userId: NumberParam, nomeEmpreend: StringParam, numeroCota: NumberParam, nomeProp: StringParam });
  const { cotaUsuarioId: cotaUsuarioIdDaRota } = useParams<{ cotaUsuarioId: string }>();
  const idDaRota = cotaUsuarioIdDaRota ? Number(cotaUsuarioIdDaRota) : undefined;
  const id = cotaUsuarioId ?? params.cotaUsuarioId ?? (Number.isFinite(idDaRota) ? idDaRota : undefined);
  const [cota, setCota] = useState<any>();
  const [periodos, setPeriodos] = useState<any[]>([]);
  const [anuncio, setAnuncio] = useState<any>();
  const [visualizacoes, setVisualizacoes] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dadosAbertos, setDadosAbertos] = useState(false);

  useEffect(() => {
    const carregar = async () => {
      if (!id) { setLoading(false); return; }
      try {
        const [cotaBasica, periodosResposta, anuncioResposta] = await Promise.all([
          buscarCotaUsuarioByIdService(id), buscarPeriodosByCotaUsuario(id), buscarAnuncioPropriedeByCotaUsuario(id),
        ]);
        console.log("[cadastroPropriedade/detalhes] resposta cota-usuario", cotaBasica);
        setCota(cotaBasica);
        setPeriodos(Array.isArray(periodosResposta) ? periodosResposta : []);
        setAnuncio(Array.isArray(anuncioResposta) ? anuncioResposta[0] : anuncioResposta);
      } catch (erro) {
        console.error("[cadastroPropriedade/detalhes] erro ao carregar informações", erro);
        setError(true);
      } finally { setLoading(false); }
    };
    carregar();
  }, [id]);

  useEffect(() => {
    const carregarVisualizacoes = async () => {
      if (!anuncio?.id) {
        setVisualizacoes(null);
        return;
      }

      const resposta = await buscarVisualizacoesByAnuncioIdService(anuncio.id);
      setVisualizacoes(Array.isArray(resposta) ? resposta.length : null);
    };

    carregarVisualizacoes();
  }, [anuncio?.id]);

  const data = cota || {};
  const empreendimentoTable = data.CotaTable?.AptoTable?.EmpreendimentoTable ?? data.AptoTable?.EmpreendimentoTable;
  const empreendimento = empreendimentoTable?.nome ?? params.nomeEmpreend ?? "Empreendimento";
  const cidade = empreendimentoTable?.CidadeTable;
  const local = cidade ? `${cidade.nome}${cidade.EstadoTab?.abreviacao ? ` - ${cidade.EstadoTab.abreviacao}` : ""}` : "Localização não informada";
  const numeroCota = data.CotaTable?.numeroCota ?? params.numeroCota ?? "—";
  const apartamento = data.numeroApto ?? data.CotaTable?.AptoTable?.nome ?? "Não informado";
  const titular = params.nomeProp ?? ([data.UsuarioTable?.nome, data.UsuarioTable?.sobrenome].filter(Boolean).join(" ") || "Não informado");
  const capacidade = data.CotaTable?.AptoTable?.capacidade ?? data.capacidade ?? "Não informado";
  const apartamentoDaCota = data.nomeApartamento ?? "Não informado";
  const numeroDaCota = data.numeroCota ?? "Não informado";
  const capacidadeDaCota = data.capacidadeApartamento;
  const capacidadeDaCotaFormatada = capacidadeDaCota !== null && capacidadeDaCota !== undefined && Number.isFinite(Number(capacidadeDaCota))
    ? `${capacidadeDaCota} pessoa${Number(capacidadeDaCota) === 1 ? "" : "s"}`
    : "Não informada";
  const titularidade = titularidadeLabel(data.titularidade?.vinculos);
  const informacoesDisponiveis = INFO_EMPREENDIMENTOS.has(empreendimento);

  const resumoPeriodos = useMemo(() => {
    const proximos = periodos.map((periodo) => {
      const periodoTable = periodo.PeriodoTable ?? periodo;
      const inicio = firstValue(periodoTable, ["checkIn", "dataInicio", "inicio", "dataIni"]);
      const fim = firstValue(periodoTable, ["checkOut", "dataFim", "fim", "dataFinal"]);
      return { inicio, fim, timestamp: new Date(String(inicio)).getTime() };
    }).filter(({ inicio, timestamp }) => inicio && !Number.isNaN(timestamp)).sort((a, b) => a.timestamp - b.timestamp);
    const proximo = proximos.find(({ timestamp }) => timestamp >= new Date().setHours(0, 0, 0, 0)) ?? proximos[0];
    const pendentes = periodos.filter((periodo) => {
      const estado = String(periodo.status ?? periodo.situacao ?? periodo.icone ?? "").toUpperCase();
      return estado.includes("SEM_STATUS") || estado.includes("NAO DEFINIDO") || estado.includes("VERIFICAR");
    }).length;
    const reservaConfirmada = periodos.some((periodo) => periodo.concluido === 1 || periodo.concluido === 2 || String(periodo.situacao ?? "").toUpperCase().includes("RESERVAD"));
    return { proximo, pendentes, reservaConfirmada };
  }, [periodos]);

  const goPeriods = () => {
    const query = new URLSearchParams({ userId: String(params.userId ?? data.usuarioId ?? 0), cotaUsuarioId: String(id), nomeEmpreend: empreendimento, numeroCota: String(numeroCota) });
    navigate(`/gerenciarPeriodosUsuarios/?${query.toString()}`);
  };
  const goToSale = () => navigate("/gerenciarAnuncioVenda", { state: { cotaUsuarioId: id, nome: empreendimento, usuarioId: data.usuarioId ?? params.userId ?? 0 } });

  if (loading) return <Page><div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Spin /></div></Page>;
  if (error || !id) return <Page><Alert type="error" message="Não foi possível carregar os detalhes desta cota." /></Page>;

  return <Page>
    <Button type="text" icon={<ArrowBackIcon />} onClick={() => navigate("/cadastroCotas")}>Minhas propriedades</Button>
    <Header><IconBox><HomeIcon /></IconBox><div><Typography.Title level={2} style={{ margin: 0 }}>{empreendimento}</Typography.Title><Typography.Text type="secondary">{local} · Cota {numeroCota}</Typography.Text><div style={{ marginTop: 10 }}><Tag color="success">{statusLabel(data.status)}</Tag></div></div></Header>

    <MainGrid gutter={[16, 16]}>
      <Col xs={24} lg={12} xl={8}><Section title={<CardTitle icon={<HomeIcon />}>Sua cota</CardTitle>}><Row gutter={[16, 18]}><Col xs={12} sm={12}><Label>Apartamento</Label><Value>{apartamentoDaCota}</Value></Col><Col xs={12} sm={12}><Label>Nº da cota</Label><Value>{numeroDaCota}</Value></Col><Col xs={12} sm={12}><Label>Titularidade</Label><Value>{titularidade}</Value></Col><Col xs={12} sm={12}><Label>Capacidade</Label><Value>{capacidadeDaCotaFormatada}</Value></Col></Row><Button type="link" style={{ paddingLeft: 0, marginTop: 18 }} onClick={() => setDadosAbertos(true)}>Ver dados da cota →</Button></Section></Col>

      <Col xs={24} lg={12} xl={16}><Section title={<CardTitle icon={<CalendarMonthIcon />}>Períodos</CardTitle>}>
        {resumoPeriodos.pendentes > 0 ? <Alert type="warning" showIcon message={`${resumoPeriodos.pendentes} período${resumoPeriodos.pendentes > 1 ? "s precisam" : " precisa"} ser informado${resumoPeriodos.pendentes > 1 ? "s" : ""}`} style={{ marginBottom: 14 }} /> : null}
        <Typography.Text strong>{periodos.length} período{periodos.length === 1 ? "" : "s"} cadastrado{periodos.length === 1 ? "" : "s"}</Typography.Text>
        {resumoPeriodos.proximo ? <Typography.Paragraph type="secondary" style={{ margin: "8px 0" }}>Próximo período: {formatDate(resumoPeriodos.proximo.inicio)} a {formatDate(resumoPeriodos.proximo.fim) ?? "data não informada"}</Typography.Paragraph> : <Typography.Paragraph type="secondary" style={{ margin: "8px 0" }}>Nenhum período informado.</Typography.Paragraph>}
        {resumoPeriodos.reservaConfirmada ? <Typography.Text type="success">✓ Reserva confirmada</Typography.Text> : null}<br />
        <Button type="link" style={{ paddingLeft: 0, marginTop: 12 }} onClick={goPeriods}>Gerenciar períodos →</Button>
      </Section></Col>

      <Col xs={24} lg={12} xl={16}><Section title={<CardTitle icon={<SellOutlinedIcon />}>Anúncio de venda</CardTitle>}>
        {anuncio ? <><Typography.Text type="success" strong>● Anúncio ativo</Typography.Text><Value>{formatCurrency(data.valorVenda ?? anuncio.valorVenda) ?? "Valor não informado"}</Value>{visualizacoes !== null ? <Typography.Text style={{ display: "block", marginTop: 8 }}>{visualizacoes} visualizações</Typography.Text> : null}{anuncio.dataAnuncio ? <Typography.Text type="secondary" style={{ display: "block", marginTop: 6 }}>Publicado em {formatDate(anuncio.dataAnuncio)}</Typography.Text> : null}<Button type="link" style={{ display: "block", paddingLeft: 0, marginTop: 12 }} onClick={goToSale}>Gerenciar anúncio →</Button></> : <><Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>Esta cota não está anunciada para venda.</Typography.Paragraph><Button type="link" style={{ paddingLeft: 0 }} onClick={() => navigate("/cadastroCotas")}>Anunciar para venda →</Button></>}
      </Section></Col>

      {informacoesDisponiveis ? <Col xs={24} lg={12} xl={8}><Section title={<CardTitle icon={<InfoOutlinedIcon />}>Informações úteis</CardTitle>}><Typography.Paragraph type="secondary">Contatos, documentos e orientações do {empreendimento}.</Typography.Paragraph><Button type="link" style={{ paddingLeft: 0, marginTop: 12 }} onClick={() => navigate(`/infoEmpreendimento/?empreend=${encodeURIComponent(empreendimento)}`)}>Acessar informações →</Button></Section></Col> : null}
    </MainGrid>

    <Drawer title="Dados da cota" open={dadosAbertos} onClose={() => setDadosAbertos(false)} width={460}><Row gutter={[16, 20]}><Col span={12}><Label>Empreendimento</Label><Value>{empreendimento}</Value></Col><Col span={12}><Label>Cota</Label><Value>{numeroCota}</Value></Col><Col span={12}><Label>Localização</Label><Value>{local}</Value></Col><Col span={12}><Label>Apartamento</Label><Value>{apartamento}</Value></Col><Col span={12}><Label>Bloco</Label><Value>{data.bloco ?? "Não informado"}</Value></Col><Col span={12}><Label>Capacidade</Label><Value>{capacidade}</Value></Col><Col span={24}><Label>Titular</Label><Value>{titular}</Value></Col></Row></Drawer>
  </Page>;
}
